import { useMutation } from "@apollo/client";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { MultipleUserPicker, type UserOption } from "components/MultipleUserPicker";
import { useCallback, useState } from "react";

import { BULK_SET_CI_ASSIGNEE } from "../api";
import { useEpcraBulkActionContext } from "./bulkActionContext";

const REFETCH = ["CredentialImplementationsForStaff", "CredentialImplementationForStaff"] as const;

export function BulkUpdateAssigneeDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, onSuccess, selectedRows } = useEpcraBulkActionContext();
  const [mode, setMode] = useState<"set" | "clear">("set");
  const [assignee, setAssignee] = useState<UserOption | null>(null);

  const [mutate, { loading }] = useMutation(BULK_SET_CI_ASSIGNEE, {
    refetchQueries: [...REFETCH],
  });

  const handleClose = useCallback(() => {
    setAssignee(null);
    setMode("set");
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (mode === "set" && !assignee) {
      alerts.error("Select a staff member or choose Clear assignee");
      return;
    }
    const ids = selectedRows.map((r) => r.id);
    try {
      const { data } = await mutate({
        variables: {
          ids,
          assigneeId: mode === "clear" ? null : (assignee?.id ?? null),
        },
      });
      alerts.success(
        `Updated assignee on ${data?.bulkSetCredentialImplementationAssignee.updated ?? 0} record(s)`
      );
      handleClose();
      onSuccess();
    } catch (e) {
      alerts.error("Could not update assignees", e);
    }
  }, [alerts, assignee, handleClose, mode, mutate, onSuccess, selectedRows]);

  const open = dialogState === "assignee";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`Update assignee (${selectedRows.length} selected)`}
      description="Set or clear the assignee on all selected credential implementation records."
      primaryActionButtonProps={{
        onClick: () => void handleSubmit(),
        disabled: loading || (mode === "set" && !assignee),
        saveText: "Save",
      }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl>
          <FormLabel id="bulk-ci-assignee-mode">Assignee action</FormLabel>
          <RadioGroup
            aria-labelledby="bulk-ci-assignee-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as "set" | "clear")}
          >
            <FormControlLabel value="set" control={<Radio />} label="Set assignee" />
            <FormControlLabel value="clear" control={<Radio />} label="Clear assignee" />
          </RadioGroup>
        </FormControl>
        {mode === "set" ? (
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Staff assignee
            </Typography>
            <MultipleUserPicker
              staffOnly
              multiple={false}
              label="Assign staff"
              value={assignee ? [assignee] : null}
              onChange={(users) => setAssignee(users?.[0] ?? null)}
            />
          </Stack>
        ) : null}
      </Stack>
    </Dialog>
  );
}
